/**
 * Admin-web API client for the CMS module's admin endpoints (006 US10).
 * Paths are under /admin/content/* (Admin module group); DTO shapes mirror the
 * C# records in Streamko.Modules.Cms.Contracts exactly.
 */
import { apiClient } from '../../../services/apiClient';

export type ContentNodeType = 'document' | 'collection' | 'entry';
export type ContentStatus = 'published' | 'hidden';

export interface ContentNodeDto {
  id: string;
  slug: string;
  name: string;
  type: ContentNodeType;
  parentId: string | null;
  sortOrder: number;
  status: ContentStatus;
  updatedAt: string;
  updatedBy: string | null;
}

export interface ContentLanguageStatusDto {
  languageCode: string;
  hasContent: boolean;
  updatedAt: string | null;
}

export interface ContentTreeNodeDto {
  node: ContentNodeDto;
  languages: ContentLanguageStatusDto[];
  children: ContentTreeNodeDto[];
}

export interface ContentTranslationDto {
  languageCode: string;
  title: string;
  bodyHtml: string;
  summary: string | null;
  rowVersion: string;
}

export interface ContentNodeDetailDto {
  node: ContentNodeDto;
  translations: ContentTranslationDto[];
}

export interface ContentAssetDto {
  url: string;
  contentType: string;
  fileSizeBytes: number;
}

export interface UpdateNodeRequest {
  status?: ContentStatus | null;
  sortOrder?: number | null;
}

export interface UpsertTranslationRequest {
  title: string;
  bodyHtml: string;
  summary?: string | null;
  rowVersion: string;
}

export interface CreateEntryTranslation {
  languageCode: string;
  title: string;
  bodyHtml: string;
  summary?: string | null;
}

export interface CreateEntryRequest {
  slug?: string | null;
  sortOrder?: number | null;
  translations: CreateEntryTranslation[];
}

export function getContentTree(): Promise<ContentTreeNodeDto[]> {
  return apiClient.get<ContentTreeNodeDto[]>('/admin/content');
}

export function getContentNode(id: string): Promise<ContentNodeDetailDto> {
  return apiClient.get<ContentNodeDetailDto>(`/admin/content/${id}`);
}

export function updateContentNode(id: string, req: UpdateNodeRequest): Promise<ContentNodeDto> {
  return apiClient.patch<ContentNodeDto>(`/admin/content/${id}`, req);
}

export function upsertTranslation(id: string, lang: string, req: UpsertTranslationRequest): Promise<ContentTranslationDto> {
  return apiClient.put<ContentTranslationDto>(`/admin/content/${id}/translations/${lang}`, req);
}

export function createEntry(collectionId: string, req: CreateEntryRequest): Promise<ContentNodeDto> {
  return apiClient.post<ContentNodeDto>(`/admin/content/${collectionId}/entries`, req);
}

export function reorderEntry(id: string, sortOrder: number): Promise<void> {
  return apiClient.patch<void>(`/admin/content/entries/${id}/reorder`, { sortOrder });
}

export function deleteEntry(id: string): Promise<void> {
  return apiClient.delete<void>(`/admin/content/entries/${id}`);
}

export function uploadContentAsset(file: File): Promise<ContentAssetDto> {
  const formData = new FormData();
  formData.append('file', file);
  return apiClient.upload<ContentAssetDto>('/admin/content/assets', formData);
}
